import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Spinner } from 'react-bootstrap';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import HomePage from './pages/home/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import NotFoundPage from './pages/system/NotFoundPage';
import ContentEditorPage from './pages/system/ContentEditorPage';
import CampaignCreatorPage from './pages/system/CampaignCreatorPage';
import RegistrationFieldEditor from './pages/system/RegistrationFieldEditor';
import UserCampaignsPage from './pages/system/UserCampaignsPage';
import PurchasesPage from './pages/system/PurchasesPage';
import CashoutPage from './pages/system/CashoutPage';
import AdminCashoutPage from './pages/system/AdminCashoutPage';

// Auth context
import { AuthProvider, useAuth } from './context/AuthContext';

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="d-flex justify-content-center p-5">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

// Admin route wrapper
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, role } = useAuth();
  
  if (isLoading) {
    return <div className="d-flex justify-content-center p-5">
      <Spinner animation="border" />
    </div>;
  }
  
  if (!isAuthenticated || role !== 'admin') {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />
      <main className="flex-grow-1 py-4">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} />
          <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" /> : <RegisterPage />} />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route path="/content-editor" element={<ContentEditorPage />} />
          <Route path="/campaign-creator" element={<CampaignCreatorPage />} />
          <Route 
            path="/system/registration-field-editor" 
            element={
              <AdminRoute>
                <RegistrationFieldEditor />
              </AdminRoute>
            } 
          />
          <Route path="/user-campaigns" element={<UserCampaignsPage />} />
          <Route path="/purchases" element={<ProtectedRoute><PurchasesPage /></ProtectedRoute>} />
<Route path="/cashout" element={<ProtectedRoute><CashoutPage /></ProtectedRoute>} />
<Route path="/admincashouts" element={<AdminCashoutPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
